import React, { useState, useEffect, useRef } from 'react'
import Api from '../../Api'
import ChatWindow from './ChatWindow'
import { useChat } from '../../context/ChatContext.tsx'
import { useEntityService } from '../../context/EntityServiceContext.tsx'
import ChatToggleButton from './ChatToggleButton'
import './FloatingChatBalloon.css'

const FloatingChatBalloon = ({ kedo }) => {
    const { isChatOpen, setIsChatOpen, chatData, setChatData, isChatEnabled } =
        useChat()

    if (!isChatEnabled) return null

    const entityService = useEntityService()
    const [messages, setMessages] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [inputValue, setInputValue] = useState('')
    const [links, setLinks] = useState([])
    const [unreadCount, setUnreadCount] = useState(0)
    const [context, setContext] = useState(null)
    const api = new Api()
    const messagesEndRef = useRef(null)
    const [selections, setSelections] = useState([])

    useEffect(() => {
        if (chatData?.dossier) {
            const saved = localStorage.getItem(
                `chat_link_selections_${chatData.dossier.def_dossier_id}`
            )
            if (saved) {
                try {
                    setSelections(JSON.parse(saved))
                } catch (e) {
                    console.error('Invalid saved selections:', e)
                    setSelections([])
                }
            }

            entityService
                .getLinkFields(chatData.dossier.def_dossier_id, {})
                .then((response) => {
                    setLinks(response.results)
                })
        }
    }, [chatData])

    // Reset unread counter when opening chat
    useEffect(() => {
        if (isChatOpen) setUnreadCount(0)
        if (!isChatOpen) setMessages([])
        if (!isChatOpen) setChatData({})
    }, [isChatOpen])

    // Add greeting
    useEffect(() => {
        if (messages.length === 0) {
            setMessages([
                {
                    id: Date.now(),
                    sender: 'bot',
                    text: '👋 Hey there! I’m **BitBean**, your data sidekick. Need help building, querying, or understanding something? ☕',
                },
            ])
        }
    }, [])

    // Auto scroll to latest message
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, [messages])

    const toggleChat = () => setIsChatOpen(!isChatOpen)

    const handleSendMessage = async (text) => {
        if (!text.trim()) return

        const userMsg = { id: Date.now(), sender: 'user', text }
        setMessages((prev) => [...prev, userMsg])
        setInputValue('')
        setIsLoading(true)

        try {
            // Build payload with environment + context info
            const payload = {
                question: text,
                environment: kedo.getCurrentEnvironmentId(),
                locale: kedo.getLanguage(),
            }

            if (context?.type === 'dossier') {
                payload.dossier_id = context.id
            } else if (context?.type === 'entity') {
                payload.entity_id = context.id
            }

            let response
            if (chatData.dossier) {
                payload.dossier_id = chatData.dossier.id
                payload.contexts = []

                if (selections.length) {
                    payload.contexts = selections.map((sel) => ({
                        path: sel.name,
                        description: sel.description || '',
                    }))
                }
                response = await api.post('/ask/test', payload)
            } else {
                response = await api.post('/ask', payload)
            }

            const botMsg = {
                id: Date.now() + 1,
                sender: 'bot',
                text: response.data.answer ?? kedo.t('received'),
            }

            setMessages((prev) => [...prev, botMsg])
            if (!isChatOpen) setUnreadCount((prev) => prev + 1)
        } catch (err) {
            console.error('Chat error:', err)
            setMessages((prev) => [
                ...prev,
                {
                    id: Date.now() + 1,
                    sender: 'bot',
                    text: kedo.t('chat.error'),
                },
            ])
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div className={`floating-chat ${isChatOpen ? 'open' : 'closed'}`}>
            <ChatToggleButton
                isOpen={isChatOpen}
                unreadCount={unreadCount}
                onClick={toggleChat}
            />

            {isChatOpen && (
                <ChatWindow
                    selections={selections}
                    setSelections={setSelections}
                    chatData={chatData}
                    links={links}
                    messages={messages}
                    isLoading={isLoading}
                    inputValue={inputValue}
                    onInputChange={setInputValue}
                    onSend={handleSendMessage}
                    messagesEndRef={messagesEndRef}
                    kedo={kedo}
                />
            )}
        </div>
    )
}

export default FloatingChatBalloon
